"use client"

import Image from "next/image"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import { useRef, useEffect, useState } from "react"

export function SpeakersPartnersPreview() {
  const speakersScrollRef = useRef<HTMLDivElement>(null)
  const partnersScrollRef = useRef<HTMLDivElement>(null)
  const [canScrollSpeakersLeft, setCanScrollSpeakersLeft] = useState(false)
  const [canScrollSpeakersRight, setCanScrollSpeakersRight] = useState(true)
  const [canScrollPartnersLeft, setCanScrollPartnersLeft] = useState(false)
  const [canScrollPartnersRight, setCanScrollPartnersRight] = useState(true)

  const allSpeakers = [
    {
      name: "Bojuwoye Emmanuel",
      role: "Growth Marketer & Automation Specialist",
      image: "/disruptED26/speakers/bojuwoye-emmanuel.jpeg",
      bio: "Tech-focused growth marketer and automation specialist. Lead at Locus Africa, expanding access to practical, future-ready digital skills across Africa.",
    },
    {
      name: "Oladotun Ajayi",
      role: "Co-founder & Social Entrepreneur",
      image: "/disruptED26/speakers/oladotun-ajayi.jpeg",
      bio: "Social entrepreneur and public health professional focused on advancing human capital development and inclusive access to opportunity.",
    },
    {
      name: "Oluwatobi Oseni",
      role: "CEO & Co-Founder (Judge)",
      image: "/disruptED26/speakers/oluwatobi-oseni.jpeg",
      bio: "Fintech innovator leading Payshiga Technologies. Judge for the Africathon competition, bringing expertise in technology and entrepreneurship.",
    },
    {
      name: "Tomide Williams",
      role: "Product Marketing Manager",
      image: "/disruptED26/speakers/tomide-williams.jpeg",
      bio: "Product marketing leader with experience in AI, SaaS, EdTech, and mobility. 2× founder with 45K+ followers.",
    },
    {
      name: "Adetomi Soyinka",
      role: "Program Officer (Panelist)",
      image: "/disruptED26/speakers/adetomi-soyinka.jpeg",
      bio: "Expert on education and economic opportunity. Panelist in the 'Future of Work' discussion, bringing perspectives on talent development.",
    },
    {
      name: "Alex Onyia",
      role: "CEO, Educare (Moderator)",
      image: "/disruptED26/speakers/alex-onyia.jpeg",
      bio: "EdTech leader and innovator. Moderator for the AI and EdTech panel, exploring artificial intelligence applications in education.",
    },
    {
      name: "Modupe Oreye",
      role: "Event Host & Moderator",
      image: "/disruptED26/speakers/modupe-oreye.jpeg",
      bio: "Seasoned compere, public speaker, and social impact advocate. Chief Operating Officer at Evolv Africa.",
    },
    {
      name: "Muhammad Abdallah",
      role: "Education Professional",
      image: "/disruptED26/speakers/muhammad-abdallah.jpeg",
      bio: "PhD in Educational Management. Head of Human Resources at Medmina College and Oyo State Secretary of STAN.",
    },
    {
      name: "Prince Lawrence Ezeh",
      role: "Honourable Commissioner",
      image: "/disruptED26/speakers/prince-lawrence-ezeh.jpeg",
      bio: "Commissioner for Innovation, Science and Technology, Enugu State Government. Leading innovation and technology initiatives.",
    },
    {
      name: "Ashley Immanuel",
      role: "Co-founder, Semicolon",
      image: "/placeholder.svg",
      bio: "Technology and education innovator. Panelist in the 'Future of Work' discussion, bringing perspectives on tech education.",
    },
  ]

  const allPartners = [
    {
      name: "University of Lagos",
      logo: "/disruptED26/partners/unilag.png",
    },
    {
      name: "Federal Ministry of Education",
      logo: "/disruptED26/partners/federal-ministry-of-education.png",
    },
    {
      name: "Ministry of Education",
      logo: "/disruptED26/partners/ministry.png",
    },
    {
      name: "University of Birmingham",
      logo: "/disruptED26/partners/university-of-birmingham-seeklogo.png",
    },
    {
      name: "Semicolon",
      logo: "/disruptED26/partners/semicolon.png",
    },
    {
      name: "Evolv Africa",
      logo: "/disruptED26/partners/evolv-a-logo.png",
    },
  ]

  // Carousel scroll functions
  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: "left" | "right") => {
    if (!ref.current) return
    const scrollAmount = 400
    if (direction === "left") {
      ref.current.scrollBy({ left: -scrollAmount, behavior: "smooth" })
    } else {
      ref.current.scrollBy({ left: scrollAmount, behavior: "smooth" })
    }
    setTimeout(checkScroll, 500)
  }

  const checkScroll = () => {
    // Check speakers scroll
    if (speakersScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = speakersScrollRef.current
      setCanScrollSpeakersLeft(scrollLeft > 0)
      setCanScrollSpeakersRight(scrollLeft < scrollWidth - clientWidth - 10)
    }
    // Check partners scroll
    if (partnersScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = partnersScrollRef.current
      setCanScrollPartnersLeft(scrollLeft > 0)
      setCanScrollPartnersRight(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }

  // Auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (speakersScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = speakersScrollRef.current
        if (scrollLeft < scrollWidth - clientWidth - 50) {
          speakersScrollRef.current.scrollBy({ left: 400, behavior: "smooth" })
        } else {
          speakersScrollRef.current.scrollTo({ left: 0, behavior: "smooth" })
        }
      }
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (partnersScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = partnersScrollRef.current
        if (scrollLeft < scrollWidth - clientWidth - 50) {
          partnersScrollRef.current.scrollBy({ left: 400, behavior: "smooth" })
        } else {
          partnersScrollRef.current.scrollTo({ left: 0, behavior: "smooth" })
        }
      }
    }, 7000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    checkScroll()
    window.addEventListener("resize", checkScroll)
    return () => window.removeEventListener("resize", checkScroll)
  }, [])

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background to-muted/50">
      <div className="container mx-auto px-4">
        <div className="space-y-24">
          {/* Featured Speakers Carousel */}
          <div className="text-center">
            <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl text-balance">
              Summit Speakers & Faculty
            </h2>
            <p className="mb-12 text-lg text-muted-foreground text-pretty mx-auto max-w-2xl">
              Meet the complete lineup of innovators, government leaders, and educators shaping Africa's future
            </p>

            <div className="relative group/carousel">
              <div
                ref={speakersScrollRef}
                className="flex gap-8 overflow-x-auto scroll-smooth pb-8 pt-4 px-4 [-webkit-scrollbar:none] [scrollbar-width:none]"
                onScroll={checkScroll}
              >
                {allSpeakers.map((speaker) => (
                  <div
                    key={speaker.name}
                    className="flex-shrink-0 w-64 md:w-80 group overflow-hidden rounded-2xl border border-border bg-slate-100/50 dark:bg-slate-800/50 transition-all hover:shadow-2xl hover:border-secondary/50"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-slate-300 dark:bg-slate-700">
                      <Image
                        src={speaker.image}
                        alt={speaker.name}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.src =
                            "/placeholder.svg?height=400&width=320"
                        }}
                      />
                    </div>
                    <div className="p-6 text-left">
                      <p className="font-bold text-foreground text-xl line-clamp-1 mb-1">
                        {speaker.name}
                      </p>
                      <p className="text-sm text-secondary font-semibold line-clamp-1 mb-3">
                        {speaker.role}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                        {speaker.bio}
                      </p>
                      <Link 
                        href="/speakers-partners" 
                        className="text-sm font-bold text-secondary hover:underline inline-flex items-center group/link"
                      >
                        Read more
                        <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Speakers Navigation Arrows */}
              <div className="flex flex-col sm:flex-row gap-6 mt-8 justify-center items-center">
                <div className="flex gap-3">
                  <button
                    onClick={() => scroll(speakersScrollRef, "left")}
                    disabled={!canScrollSpeakersLeft}
                    className="p-3 rounded-xl border border-border bg-card hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                    aria-label="Scroll left"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={() => scroll(speakersScrollRef, "right")}
                    disabled={!canScrollSpeakersRight}
                    className="p-3 rounded-xl border border-border bg-card hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>
                <Link href="/speakers-partners">
                  <Button variant="outline" size="lg" className="group rounded-xl border-secondary text-secondary hover:bg-secondary hover:text-white px-8">
                    View All Speakers & Judges
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Strategic Partners Carousel */}
          <div className="text-center">
            <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl text-balance">
              Strategic Partners
            </h2>
            <p className="mb-12 text-lg text-muted-foreground text-pretty mx-auto max-w-2xl">
              Collaborating with prestigious institutions and government bodies to drive educational transformation
            </p>

            <div className="relative group/carousel">
              <div
                ref={partnersScrollRef}
                className="flex gap-8 overflow-x-auto scroll-smooth pb-8 pt-4 px-4 [-webkit-scrollbar:none] [scrollbar-width:none]"
                onScroll={checkScroll}
              >
                {allPartners.map((partner) => (
                  <div
                    key={partner.name}
                    className="flex-shrink-0 w-64 md:w-80 flex flex-col items-center justify-center overflow-hidden rounded-2xl border border-border bg-slate-100/50 dark:bg-slate-800/50 p-10 transition-all hover:shadow-xl hover:border-secondary/30"
                  >
                    <div className="relative w-full aspect-video">
                      <Image
                        src={partner.logo}
                        alt={partner.name}
                        fill
                        className="object-contain"
                        onError={(e) => {
                          e.currentTarget.src =
                            "/placeholder.svg?height=80&width=150"
                        }}
                      />
                    </div>
                    <p className="mt-6 text-center font-bold text-foreground text-sm uppercase tracking-wider">
                      {partner.name}
                    </p>
                  </div>
                ))}
              </div>

              {/* Partners Navigation Arrows */}
              <div className="flex flex-col sm:flex-row gap-6 mt-8 justify-center items-center">
                <div className="flex gap-3">
                  <button
                    onClick={() => scroll(partnersScrollRef, "left")}
                    disabled={!canScrollPartnersLeft}
                    className="p-3 rounded-xl border border-border bg-card hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                    aria-label="Scroll left"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={() => scroll(partnersScrollRef, "right")}
                    disabled={!canScrollPartnersRight}
                    className="p-3 rounded-xl border border-border bg-card hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>
                <Link href="/speakers-partners#partners">
                  <Button variant="outline" size="lg" className="group rounded-xl border-secondary text-secondary hover:bg-secondary hover:text-white px-8">
                    View All Partners
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
